import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";
import { GtmCustomEvent, GtmService } from "@/services/api/gtm-service";

const buttonVariants = cva(
  "leading-tight inline-flex items-center justify-center scale-100 active:scale-[0.98] hover:scale-[1.02] transform  ease-in-out duration-300 rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        warning:
          "bg-warning text-warning-foreground shadow-sm hover:bg-warning/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        borderMagic:
          "relative !h-12 overflow-hidden !rounded-full !p-[3px] focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50",
      },
      size: {
        default: "text-lg text-sm h-9 px-4 py-2",
        xs: "h-6 rounded-sm text-xs px-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-4 md:px-8",
        xl: "h-14 rounded-md px-4 md:px-10 text-lg",
        xxl: "h-16 rounded-md px-6 md:px-12 text-lg md:text-xl",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  gtmEvent?: GtmCustomEvent;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, gtmEvent, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    const onClickWithMetrics = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!gtmEvent) {
        return props.onClick?.(e);
      }

      GtmService.push(gtmEvent);

      props.onClick?.(e);
    };

    if (variant === "borderMagic") {
      const { children, ...rest } = props;
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, className }))}
          ref={ref}
          {...rest}
          onClick={onClickWithMetrics}
        >
          {variant === "borderMagic" && (
            <>
              <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#496B38_0%,#811D1D_50%,#496B38_100%)]" />
              <span className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-full bg-primary px-4 py-1 text-lg font-medium text-primary-foreground backdrop-blur-3xl md:px-6 md:py-3 md:text-xl">
                {children}
              </span>
            </>
          )}
        </Comp>
      );
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        onClick={onClickWithMetrics}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
